import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel } from '@/hooks/useSaveShortcut';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { getErrorMessage } from '@/lib/apiClient';
import { checkOfflineAndNotify } from '@/lib/offlineGuard';
import { PermissionTree, PermissionTreeHandle } from '@/components/PermissionTree';
import { useRole, useCreateRole, useUpdateRole } from '@/hooks/identity/useIdentity';

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const { data: role, isLoading } = useRole(id!, { enabled: !isNew });
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const permRef = useRef<PermissionTreeHandle>(null);

  const { t } = useTranslation();
  const [apiErrors, setApiErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    if (role) {
      setName(role.name);
      setIsDefault(role.isDefault);
      setIsPublic(role.isPublic);
    }
  }, [role]);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isStatic = role?.isStatic ?? false;

  const handleSave = () => {
    if (checkOfflineAndNotify(setApiErrors, setValidationPanelOpen, t)) return;
    setApiErrors([]);

    // Save permissions if changed (independent of role detail save)
    if (!isNew && permRef.current?.hasChanges) {
      permRef.current.save();
    }

    if (isNew) {
      createMutation.mutate(
        { name, isDefault, isPublic },
        {
          onSuccess: (created) => {
            navigate(`/core/roles/${created.id}`, { replace: true });
          },
          onError: (error: unknown) => {
            const errors = getValidationErrors(error);
            if (errors.length > 0) {
              setApiErrors(errors);
              setValidationPanelOpen(true);
            } else {
              setApiErrors([{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: [] }]);
              setValidationPanelOpen(true);
            }
          },
        },
      );
    } else {
      updateMutation.mutate(
        {
          id: id!,
          data: { name, isDefault, isPublic, concurrencyStamp: role?.concurrencyStamp },
        },
        {
          onSuccess: () => { setApiErrors([]); },
          onError: (error: unknown) => {
            const errors = getValidationErrors(error);
            if (errors.length > 0) {
              setApiErrors(errors);
              setValidationPanelOpen(true);
            } else {
              setApiErrors([{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: [] }]);
              setValidationPanelOpen(true);
            }
          },
        },
      );
    }
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const saveDisabled = isSaving || !name;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ValidationErrorBanner
        errors={apiErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => { setApiErrors([]); setValidationPanelOpen(false); }}
      />

      <ViewToolbar showBackButton onBack={() => navigate('/core/roles')} onSave={handleSave} saveDisabled={saveDisabled}>
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                ...(saveDisabled
                  ? { borderColor: 'grey.300', color: 'grey.400' }
                  : { borderColor: 'primary.main', color: 'primary.main' }),
              }}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {isNew ? t('identity.createRole') : t('identity.editRole', { name: role?.name ?? '' })}
          </Typography>

          <JsonFormsThemeProvider density="compact" variant="standard">
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('identity.roleDetails')}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label={t('identity.roleName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  size="small"
                  disabled={isStatic}
                  helperText={isStatic ? t('identity.staticRoleRenameHint') : undefined}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                <FormControlLabel
                  control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                  label={t('identity.isDefault')}
                />
                <FormControlLabel
                  control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
                  label={t('identity.isPublic')}
                />
              </Box>
            </Paper>

            {/* Permission Tree — only show for existing roles */}
            {!isNew && role && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>{t('identity.permissions')}</Typography>
                <PermissionTree ref={permRef} roleName={role.name} />
              </Paper>
            )}

            {isNew && (
              <Alert severity="info">
                {t('identity.saveRoleFirst')}
              </Alert>
            )}
          </JsonFormsThemeProvider>
        </Box>

        <ValidationErrorPanel
          errors={apiErrors}
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
        />
      </Box>
    </Box>
  );
}
